// Zweck: Mengen auf dem Handy per großer Taste oder Zahleneingabe ändern.
// Kontext: Keine automatische Entfernung; Dezimalmengen behalten ihre Nachkommastellen. Stand: 20.10.2026.
import { useEffect, useState } from 'react';

const parse = (text: string) =>
  /^\d+([,.]\d{1,3})?$/.test(text.trim())
    ? Number(text.trim().replace(',', '.'))
    : NaN;
const format = (value: number) =>
  Number.isFinite(value) ? String(value).replace('.', ',') : '';

export default function WarehouseQuantity({
  id,
  name,
  unit,
  fractional,
  value,
  disabled,
  onChange,
  onNext,
  allowEmptyIncrement = false,
}: {
  id: string;
  name: string;
  unit: string;
  fractional: boolean;
  value: number;
  disabled: boolean;
  onChange: (value: number) => void;
  onNext?: () => void;
  allowEmptyIncrement?: boolean;
}) {
  const [text, setText] = useState(() => format(value));
  useEffect(() => {
    // Eingaben wie „2,“ beim Tippen erhalten, externe Änderungen dagegen anzeigen.
    setText((current) =>
      Object.is(parse(current), value) ? current : format(value),
    );
  }, [value]);
  const valid =
    Number.isFinite(value) &&
    value > 0 &&
    (fractional || Number.isInteger(value));
  const step = (direction: number) => {
    const next =
      allowEmptyIncrement && !text.trim() && direction > 0
        ? 1
        : (Math.round(value * 1000) + direction * 1000) / 1000;
    setText(format(next));
    onChange(next);
  };
  return (
    <>
      <label htmlFor={'qty-' + id}>Menge ({unit})</label>
      <div
        className="wh-quantity"
        role="group"
        aria-label={'Menge für ' + name}
      >
        <button
          type="button"
          aria-label={`${name}: Menge um 1 ${unit} verringern`}
          disabled={disabled || !valid || value <= 1}
          onClick={() => step(-1)}
        >
          −
        </button>
        <input
          id={'qty-' + id}
          disabled={disabled}
          aria-invalid={!valid}
          aria-describedby={'hint-' + id}
          inputMode={fractional ? 'decimal' : 'numeric'}
          enterKeyHint={onNext ? 'next' : 'done'}
          autoComplete="off"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            onChange(parse(event.target.value));
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && onNext) {
              event.preventDefault();
              onNext();
            }
          }}
        />
        <button
          type="button"
          aria-label={`${name}: Menge um 1 ${unit} erhöhen`}
          disabled={
            disabled || (!valid && !(allowEmptyIncrement && !text.trim()))
          }
          onClick={() => step(1)}
        >
          +
        </button>
      </div>
    </>
  );
}
